import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { monitoringApi, Classification, MonitoringApiError } from '../../../services/monitoringApi';

interface MonitoringDeactivateDialogProps {
    isOpen: boolean;
    item: Classification;
    onClose: () => void;
    onSuccess: () => void;
}

const MonitoringDeactivateDialog: React.FC<MonitoringDeactivateDialogProps> = ({ isOpen, item, onClose, onSuccess }) => {
    const [notes, setNotes] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [apiError, setApiError] = useState('');

    const handleClose = () => {
        setNotes('');
        setApiError('');
        onClose();
    };

    const handleConfirm = async () => {
        setIsSubmitting(true);
        setApiError('');
        try {
            await monitoringApi.deactivate(item.person_id, {
                notes: notes.trim() || undefined,
            });
            toast.success('Monitoramento desativado com sucesso');
            onSuccess();
            handleClose();
        } catch (e: unknown) {
            if (e instanceof MonitoringApiError) {
                setApiError(e.message);
            } else {
                setApiError('Erro ao desativar monitoramento');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md mx-4">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-slate-700">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Desativar Monitoramento
                    </h2>
                </div>

                {/* Body */}
                <div className="px-6 py-4 space-y-4">
                    <div className="flex items-center gap-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-md">
                        <span className="text-yellow-600 dark:text-yellow-400 text-lg">&#9888;</span>
                        <p className="text-sm text-yellow-800 dark:text-yellow-300">
                            Tem certeza que deseja desativar o monitoramento?
                        </p>
                    </div>

                    <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                        <p><span className="font-medium">Pessoa:</span> {item.person_id}</p>
                        <p><span className="font-medium">Risco:</span> {item.risk_level}</p>
                    </div>

                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Após desativar, o documento não será mais incluído nas varreduras diárias.
                    </p>

                    <div>
                        <label htmlFor="deactivateNotes" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Observação
                        </label>
                        <textarea
                            id="deactivateNotes"
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            placeholder="Motivo da desativação (opcional)"
                            rows={3}
                            maxLength={500}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-md text-sm bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        />
                        <p className="text-xs text-gray-500 dark:text-gray-400 text-right mt-1">
                            {notes.length}/500
                        </p>
                    </div>

                    {apiError && (
                        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-md">
                            <p className="text-sm text-red-800 dark:text-red-300">{apiError}</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 flex justify-end gap-3">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md hover:bg-gray-50 dark:hover:bg-slate-600"
                    >
                        Cancelar
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={isSubmitting}
                        className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? 'Desativando...' : 'Desativar'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MonitoringDeactivateDialog;
